import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Product Customization Component
 * Manages customization options for specific products
 * 
 * @extends Component
 */
class ProductCustomizationComponent extends Component {
  constructor() {
    super();
    
    this.customizationPrice = parseFloat(this.dataset.customizationPrice || '2.50') || 2.50;
    /** @type {HTMLInputElement | null} */
    this.checkbox = /** @type {HTMLInputElement | null} */ (this.querySelector('#customization-enabled'));
    /** @type {HTMLElement | null} */
    this.fields = this.querySelector('.product-customization__fields');
    /** @type {HTMLElement | null} */
    this.priceSummary = this.querySelector('.product-customization__price-summary');
    /** @type {HTMLElement | null} */
    this.hint = this.querySelector('.product-customization__checkbox-hint');
    /** @type {HTMLInputElement | null} */
    this.priceAddonInput = /** @type {HTMLInputElement | null} */ (this.querySelector('#customization-price-addon'));
    
    /** @type {HTMLInputElement | null} */
    this.customTextInput = /** @type {HTMLInputElement | null} */ (this.querySelector('#customization-text'));
    /** @type {HTMLInputElement | null} */
    this.threadColorInput = /** @type {HTMLInputElement | null} */ (this.querySelector('#customization-thread-color'));
    
    /** @type {HTMLFormElement | null} */
    this.form = /** @type {HTMLFormElement | null} */ (this.closest('form'));
    /** @type {HTMLElement | null} */
    this.variantPicker = null;
    /** @type {string | null} */
    this.originalPrice = null;
    this.supplementVariantId = this.dataset.supplementVariantId;
    this.pendingSupplement = false;
    this.pendingSupplementQuantity = 1;
    /** @type {{ text: string; color: string } | null} */
    this.pendingCustomizationData = null;
    /** @type {Record<string, string> | null} */
    this.pendingSections = null;
    /** @type {((event: CustomEvent) => void) | null} */
    this.handleCartUpdateBound = null;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Wait for variant picker to be ready
    setTimeout(() => {
      this.variantPicker = this.form ? this.form.querySelector('variant-picker-component') : null;
      this.enableCheckbox();
      this.setupEventListeners();
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.handleCartUpdateBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.handleCartUpdateBound);
    }
  }

  setupEventListeners() {
    // Listen to checkbox changes
    this.checkbox?.addEventListener('change', () => this.handleCheckboxChange());
    
    // Listen to variant changes
    if (this.variantPicker) {
      document.addEventListener(ThemeEvents.variantUpdate, () => this.checkVariantSelection());
    }

    this.handleCartUpdateBound = this.#handleCartUpdate.bind(this);
    if (this.handleCartUpdateBound) {
      document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdateBound);
    }
    
    // Listen to required field changes
    [this.customTextInput, this.threadColorInput].forEach(field => {
      field?.addEventListener('change', () => this.validateFields());
    });
    
    // Intercept form submission to validate customization fields
    this.form?.addEventListener('submit', (e) => this.handleFormSubmit(e));
  }

  /**
   * Check if a variant (color) has been selected
   */
  checkVariantSelection() {
    const variantInput = this.form?.querySelector('[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : null;
    const hasVariant = variantId && variantId !== '';
    
    if (hasVariant) {
      this.enableCheckbox();
    } else {
      this.disableCheckbox();
    }
  }

  /**
   * Handle checkbox change
   */
  handleCheckboxChange() {
    if (this.checkbox?.checked) {
      this.showCustomizationFields();
      this.updatePriceDisplay(true);
    } else {
      this.hideCustomizationFields();
      this.updatePriceDisplay(false);
      this.clearCustomizationFields();
    }
  }

  /**
   * Show customization fields
   */
  showCustomizationFields() {
    if (this.fields) {
      this.fields.style.display = 'flex';
      
      // Enable all fields
      if (this.customTextInput) {
        this.customTextInput.disabled = false;
        this.customTextInput.required = true;
      }
      if (this.threadColorInput) {
        this.threadColorInput.disabled = false;
        this.threadColorInput.required = true;
      }
      
      // Make fields required
    }
    
    if (this.priceSummary) {
      this.priceSummary.style.display = 'flex';
    }
  }

  /**
   * Hide customization fields
   */
  hideCustomizationFields() {
    if (this.fields) {
      this.fields.style.display = 'none';
      
      // Disable all fields
      if (this.customTextInput) {
        this.customTextInput.disabled = true;
        this.customTextInput.required = false;
      }
      if (this.threadColorInput) {
        this.threadColorInput.disabled = true;
        this.threadColorInput.required = false;
      }
    }
    
    if (this.priceSummary) {
      this.priceSummary.style.display = 'none';
    }
  }

  /**
   * Clear customization fields
   */
  clearCustomizationFields() {
    if (this.customTextInput) this.customTextInput.value = '';
    if (this.threadColorInput) this.threadColorInput.value = '';
    if (this.priceAddonInput) this.priceAddonInput.value = '';
  }

  /**
   * Validate customization fields
   */
  validateFields() {
    if (!this.checkbox?.checked) return true;
    
    const textValid = (this.customTextInput?.value.trim() || '') !== '';
    const colorValid = (this.threadColorInput?.value.trim() || '') !== '';
    
    return textValid && colorValid;
  }

  /**
   * Update price display with customization addon
   * @param {boolean} addCustomization
   */
  updatePriceDisplay(addCustomization) {
    const priceElement = this.form?.querySelector('.price__current');
    
    if (!priceElement) return;
    
    // Store original price on first run
    if (!this.originalPrice) {
      this.originalPrice = priceElement.textContent;
    }
    
    if (addCustomization) {
      // Add customization price to display
      const currentPriceText = priceElement.textContent || '';
      const priceMatch = currentPriceText.match(/[\d,]+(?:[.,]\d{2})?/);
      
      if (priceMatch) {
        const currentPrice = parseFloat(priceMatch[0].replace(',', '.'));
        const newPrice = currentPrice + this.customizationPrice;
        const formattedPrice = newPrice.toFixed(2).replace('.', ',');
        
        // Update display
        priceElement.textContent = priceElement.textContent.replace(
          priceMatch[0],
          formattedPrice
        );
      }
      
      // Set hidden field value for cart
      if (this.priceAddonInput) {
        this.priceAddonInput.value = `${this.customizationPrice.toFixed(2)} €`;
      }
    } else {
      // Restore original price
      if (this.originalPrice) {
        priceElement.textContent = this.originalPrice;
      }
      
      // Clear hidden field
      if (this.priceAddonInput) {
        this.priceAddonInput.value = '';
      }
    }
  }

  /**
   * Handle form submission
   * @param {SubmitEvent} event
   */
  handleFormSubmit(event) {
    if (this.checkbox?.checked && !this.validateFields()) {
      event.preventDefault();
      event.stopPropagation();
      
      alert('Veuillez remplir tous les champs de personnalisation obligatoires.');
      
      // Focus first empty field
      if (this.customTextInput && !this.customTextInput.value.trim()) {
        this.customTextInput.focus();
      } else if (this.threadColorInput && !this.threadColorInput.value.trim()) {
        this.threadColorInput.focus();
      }
      
      return false;
    }
    
    if (this.checkbox?.checked) {
      this.pendingSupplement = true;
      const quantityInput = this.form?.querySelector('[name="quantity"]');
      const quantityValue = quantityInput instanceof HTMLInputElement ? quantityInput.value : '1';
      this.pendingSupplementQuantity = Number(quantityValue) || 1;
      this.pendingCustomizationData = {
        text: this.customTextInput?.value.trim() || '',
        color: this.threadColorInput?.value.trim() || '',
      };
    } else {
      this.pendingSupplement = false;
      this.pendingCustomizationData = null;
    }
    
    return true;
  }

  enableCheckbox() {
    if (this.checkbox) {
      this.checkbox.disabled = false;
    }
    this.hint?.classList.add('hidden');
  }

  disableCheckbox() {
    if (this.checkbox) {
      this.checkbox.disabled = true;
      this.checkbox.checked = false;
    }
    this.hint?.classList.remove('hidden');
    this.hideCustomizationFields();
    this.clearCustomizationFields();
    this.pendingSupplement = false;
    this.pendingCustomizationData = null;
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    if (!this.pendingSupplement || !this.supplementVariantId) return;

    const detail = event.detail?.data;
    if (!detail || detail.didError) {
      this.pendingSupplement = false;
      return;
    }

    if (detail.source !== 'product-form-component') return;
    if (detail.productId !== this.dataset.productId) return;

    this.pendingSections = detail.sections || null;
    this.pendingSupplement = false;
    this.#addSupplementToCart();
  };

  async #addSupplementToCart() {
    try {
      const sectionsList = this.pendingSections ? Object.keys(this.pendingSections) : null;

      /** @type {{
       *   items: Array<{
       *     id: string | undefined;
       *     quantity: number;
       *     properties: Record<string, string>;
       *   }>;
       *   sections?: string;
       * }} */
      const body = {
        items: [
          {
            id: this.supplementVariantId,
            quantity: this.pendingSupplementQuantity,
            properties: {
              'Texte personnalisé': this.pendingCustomizationData?.text || '',
              'Couleur du fil': this.pendingCustomizationData?.color || '',
              '_Supplément pour': this.dataset.productId || '',
            },
          },
        ],
      };

      if (sectionsList?.length) {
        body.sections = sectionsList.join(',');
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
      const json = await response.json();

      document.dispatchEvent(
        new CartAddEvent(json, this.dataset.productId || '', {
          source: 'product-customization',
          productId: this.dataset.productId,
          itemCount: this.pendingSupplementQuantity,
          sections: json.sections,
        })
      );
    } catch (/** @type {unknown} */ error) {
      console.error('Erreur lors de l’ajout du supplément de personnalisation :', error);
    } finally {
      this.pendingCustomizationData = null;
      this.pendingSections = null;
    }
  }

}

// Register the custom element
if (!customElements.get('product-customization-component')) {
  customElements.define('product-customization-component', ProductCustomizationComponent);
}

